import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchUsers } from "../../store/features/user/userSlice";
import { Row, Col, Card, Badge, ListGroup, Button } from "react-bootstrap";
import PageTitle from "../../components/pageTitle";
import RolesPieChart from "./components/RolesPieChart";
import {
  getUserRoleStats,
  getProjectWpTaskStats,
  getTodaysMeetings,
} from "../../store/features/dashboard/dashboardSlice";
import ProjectWpTaskPieChart from "./components/ProjectWpTaskChart";
import CardData from "./components/CardData";
import { FaUsers } from "react-icons/fa6";
import { FaFolderOpen } from "react-icons/fa6";
import { FaTasks } from "react-icons/fa";
import { TbPackages } from "react-icons/tb";
import { SiGooglemeet } from "react-icons/si";
import { IoMdNotifications } from "react-icons/io";
import ProjectTable from "../projectManagement/projects/components/ProjectTable";
import WorkPackageTable from "../projectManagement/workPackages/components/WorkPackageTable";
import TaskTable from "../projectManagement/tasks/components/TaskTable";
import { fetchProjects } from "../../store/features/project/projectSlice";
import NoDataFound from "../../components/NoDataFound";
import { Spinner } from "react-bootstrap";
function Dashboard() {
  const dispatch = useDispatch();
  const { unreadCount } = useSelector((state) => state.notifications);
  const { usreRolesStats, loading, projectWpTaskStats, todaysMeetings } =
    useSelector((state) => state.dashboard);
  useEffect(() => {
    dispatch(getUserRoleStats());
    dispatch(getProjectWpTaskStats());
    dispatch(getTodaysMeetings());
  }, []);
  const { role, _id, name } = useSelector((state) => state.auth);
  const { projects } = useSelector((state) => state.projects);

  // const projects = useSelector((state) =>
  //   state.projects.filter((project) => project.assignedTo === user.id)
  // );
  const { workPackages } = useSelector((state) => state.workPackages);
  const { tasks } = useSelector((state) => state.tasks);
  const { assistantSuperAdmin, managers, users } = useSelector(
    (state) => state.users
  );
  const { meetings } = useSelector((state) => state.meetings);

  const getProgessLevelStatus = (progress = 0) => {
    if (progress === 0) return "Pending";
    else if (progress < 50) return "At Risk";
    else if (progress < 100) return "On Track";
    else return "Completed";
  };

  const visibleColumns = useMemo(() => {
    const allColumns = [
      {
        header: "Project Name",
        accessor: "projectName",
        roles: ["superadmin", "assistant superadmin", "auditor"],
      },
      {
        header: "Assigned To",
        accessor: "assistantSuperAdminId",
        roles: ["superadmin", "auditor"],
        cell: (project) => {
          return project.assistantSuperadmin?.name;
        },
      },
      {
        header: "Assigned By",
        accessor: "assignedBy",
        roles: ["assistant superadmin", "auditor"],
        cell: (project) => {
          return project.creator?.name;
        },
      },

      {
        header: "Work Package Name",
        accessor: "wpName",
        roles: ["manager"],
        cell: (wp) => {
          return wp.title;
        },
      },
      {
        header: "Assigned By",
        accessor: "wpAssign",
        roles: ["manager"],
        cell: (wp) => {
          return assistantSuperAdmin?.find(
            (u) => u._id === wp.project.assistantSuperAdminId
          )?.name;
        },
      },
      {
        header: "Progress",
        accessor: "wpProgress",
        roles: ["manager"],
        cell: (wp) => {
          return (wp.progressLevel || 0) + "%";
        },
      },
      {
        header: "Status",
        accessor: "wpStatus",
        roles: ["manager"],
        cell: (wp) => {
          return (
            <Badge
              className="mt-1"
              bg={
                (wp.progressLevel || 0) < 50
                  ? "danger"
                  : (wp.progressLevel || 0) < 100
                  ? "info"
                  : "success"
              }
            >
              {getProgessLevelStatus(wp.progressLevel)}
            </Badge>
          );
        },
      },

      {
        header: "Task Name",
        accessor: "taskName",
        roles: ["staff"],
        cell: (task) => {
          return task.title;
        },
      },
      {
        header: "Assigned By",
        accessor: "taskAssignedBy",
        roles: ["staff"],
        cell: (task) => {
          return task?.assignedByUser?.name;
        },
      },
      {
        header: "Progress",
        accessor: "progress",
        roles: ["superadmin", "assistant superadmin", "auditor"],
        cell: (project) => {
          return (project.progressLevel || 0) + "%";
        },
      },
      {
        header: "Status",
        accessor: "status",
        roles: ["superadmin", "assistant superadmin", "auditor"],
        cell: (project) => {
          return (
            <Badge
              className="mt-1"
              bg={
                (project.progressLevel || 0) < 50
                  ? "danger"
                  : (project.progressLevel || 0) < 100
                  ? "info"
                  : "success"
              }
            >
              {getProgessLevelStatus(project.progressLevel)}
            </Badge>
          );
        },
      },
      {
        header: "Deadline",
        accessor: "endDate",
        roles: [
          "superadmin",
          "assistant superadmin",
          "manager",
          "staff",
          "auditor",
        ],
        cell: (item) => {
          const date = item.endDate ? new Date(item.endDate) : null;
          return date
            ? date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })
            : "-";
        },
      },
    ];

    return allColumns.filter((col) => col.roles.includes(role));
  }, [role, managers, assistantSuperAdmin, users]);

  const statusBadge = {
    "On Track": "success",
    "At Risk": "warning",
    Delayed: "danger",
  };

  const dashboardCardData = [
    {
      key: "users",
      icon: <FaUsers color="#d22e2d" />,
      count: usreRolesStats?.reduce((acc, data) => acc + data.count, 0) || 0,
      label: "Total Users",
      roles: ["superadmin", "assistant superadmin", "manager", "auditor"],
      link: "/user-management",
    },
    {
      key: "projects",
      icon: <FaFolderOpen color="#c607c6" />,
      count: projectWpTaskStats?.projectCount || 0,
      label: "Total Projects",
      roles: ["superadmin", "assistant superadmin", "auditor"],
      link: "/project-management",
    },
    {
      key: "workpackages",
      icon: <TbPackages color="#ee6b01" />,
      count: projectWpTaskStats?.workpackageCount || 0,
      label: "Total Work packages",
      roles: ["superadmin", "assistant superadmin", "manager", "auditor"],
      link: "/project-management",
    },
    {
      key: "tasks",
      icon: <FaTasks color="#069d57" />,
      count: projectWpTaskStats?.taskCount || 0,
      label: "Total Tasks",
      roles: [
        "superadmin",
        "assistant superadmin",
        "manager",
        "staff",
        "auditor",
      ],
      link: "/project-management",
    },
    {
      key: "notifications",
      icon: <IoMdNotifications color="#dc3545" />,
      count: unreadCount || 0,
      label: "New Notifications",
      roles: [
        "superadmin",
        "assistant superadmin",
        "manager",
        "staff",
        "auditor",
      ],
      link: "/notifications",
    },
    {
      key: "meetings",
      icon: <SiGooglemeet color="#069d57" />,
      count: todaysMeetings?.length || 0,
      label: "Today's Meetings",
      roles: [
        "superadmin",
        "assistant superadmin",
        "manager",
        "staff",
        "auditor",
      ],
      link: "/meeting",
    },
  ];
  const dashboardCardDataByRole = dashboardCardData.filter((card) =>
    card.roles.includes(role)
  );
  useEffect(() => {
    dispatch(getUserRoleStats());
    dispatch(getProjectWpTaskStats());
    dispatch(getTodaysMeetings());

    if (!projects || projects.length === 0) {
      dispatch(fetchProjects());
      dispatch(fetchUsers());
    }
    document.querySelector('#google_translate_element').removeAttribute('style');
  }, []);

  return (
    <div className="dashboard">
      <PageTitle
        pageTitle="Dashboard"
        pageSubtitle="All Your Insights in One Place."
      />

      <>
        <Row className="mb-4">
          <CardData data={dashboardCardDataByRole} />
        </Row>
        {/* Active Projects Table */}

        {role === "superadmin" && (
          <Card className="mb-4 shadow-sm">
            <Card.Header>
              <strong>Active Projects</strong>
            </Card.Header>
            <ProjectTable
              projects={projects?.slice(0, 5) || []}
              columns={visibleColumns}
            />
          </Card>
        )}
        {role === "assistant superadmin" && (
          <Card className="mb-4 shadow-sm">
            <Card.Header>
              <strong>Assigned Projects</strong>
            </Card.Header>
            <ProjectTable
              projects={projects?.slice(0, 5) || []}
              columns={visibleColumns}
            />
          </Card>
        )}
        {role === "manager" && (
          <Card className="mb-4 shadow-sm">
            <Card.Header>
              <strong>Assigned Workpackages</strong>
            </Card.Header>
            <WorkPackageTable
              workpackages={workPackages?.slice(0, 5) || []}
              columns={visibleColumns}
            />
          </Card>
        )}
        {role === "staff" && (
          <Card className="mb-4 shadow-sm">
            <Card.Header>
              <strong>Assigned Tasks</strong>
            </Card.Header>
            <TaskTable
              tasks={tasks?.slice(0, 5) || []}
              columns={visibleColumns}
            />
          </Card>
        )}
        <Row>{/* Today's Meetings */}</Row>
        <Row className="mb-4">
          {role !== "staff" && (
            <Col md={4}>
              <Card className="shadow-sm h-100">
                <Card.Header>
                  <strong>User Roles Distribution</strong>
                </Card.Header>
                <Card.Body>
                  <RolesPieChart roles={usreRolesStats} />
                </Card.Body>
              </Card>
            </Col>
          )}

          <Col md={4}>
            <Card className="shadow-sm h-100">
              <Card.Header>
                <strong>Work Distribution</strong>
              </Card.Header>
              <Card.Body>
                <ProjectWpTaskPieChart stats={projectWpTaskStats} role={role} />
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="shadow-sm h-100">
              <Card.Header className="d-flex justify-content-between">
                <strong>Today's Meetings</strong>
              </Card.Header>
              <ListGroup variant="flush">
                {todaysMeetings.length > 0 ? (
                  <>
                    {todaysMeetings.map((meeting, i) => (
                      <ListGroup.Item key={i}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{meeting.time}</strong> - {meeting.title}{" "}
                            <span className="small text-muted">
                              ({meeting.meetingType})
                            </span>
                            <div className="small">
                              Project:{" "}
                              <span className="text-muted">
                                {meeting?.projectId?.projectName}
                              </span>
                            </div>
                            <div className="small">
                              Attendees:{" "}
                              <span className="text-muted">
                                {meeting?.attendeesIds?.map((u) => (
                                  <Badge className="me-1" bg="secondary">
                                    {u.name}
                                  </Badge>
                                ))}
                              </span>
                            </div>
                            <div className="small">
                              Objective:{" "}
                              <span className="text-muted">
                                {meeting?.objective}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-center mt-3">
                          <Button bg="light" text="dark">
                            <a
                              style={{
                                textDecoration: "none",
                                color: "#fff",
                              }}
                              href={meeting.meetingLink}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {meeting.meetingPlatform}
                            </a>
                          </Button>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </>
                ) : (
                  <NoDataFound
                    icon={<SiGooglemeet size={50} />}
                    title="Today no meetings found"
                    description="Try to add some meetings."
                  />
                )}
              </ListGroup>
            </Card>
          </Col>
        </Row>
      </>
    </div>
  );
}

export default Dashboard;
